import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Zap, 
  ArrowLeft, 
  Download, 
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  FileText,
  Mail,
  Building2,
  MapPin,
  Briefcase,
  Calendar,
  ExternalLink,
  Share2,
  Copy,
  Check,
  Search,
  Star
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("https://cal.com/mark-earle-6tzgnf/15min");
  const [email, setEmail] = useState("");
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [copied, setCopied] = useState(false);
  const reportRef = useRef(null);

  const shareUrl = `${window.location.origin}/share/${auditId}`;

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchAudit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audit/${auditId}`);
      setAudit(response.data);
    } catch (error) {
      console.error("Failed to fetch audit:", error);
      toast.error("Failed to load audit report");
      navigate("/audit");
    } finally {
      setLoading(false);
    }
  }, [auditId, navigate]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const handleUnlock = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmittingEmail(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        email,
        audit_id: auditId,
        business_name: audit.business_name,
        service_type: audit.service_type,
        location: audit.location
      });
      
      // Get booking URL from response
      if (response.data.booking_url) {
        setBookingUrl(response.data.booking_url);
      }
      
      setIsUnlocked(true);
      setShowEmailModal(false);
      setShowThankYou(true);
    } catch (error) {
      console.error("Failed to capture lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmittingEmail(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/report/${auditId}/pdf`);
      const { html } = response.data;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast.error("Failed to generate PDF report");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 60) return "text-[#d97706]";  // Orange for 60+
    return "text-[#dc2626]";  // Red for below 60
  };

  const getScoreStatus = (score) => {
    if (score >= 80) return { label: "Minor Gaps - Quick Wins Available", color: "bg-[#f59e0b]/10 text-[#d97706] border-[#f59e0b]/20" };
    if (score >= 60) return { label: "Improvement Needed", color: "bg-[#f59e0b]/10 text-[#d97706] border-[#f59e0b]/20" };
    if (score >= 40) return { label: "Significant Gaps", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
    if (score >= 20) return { label: "Critical Issues", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
    return { label: "Urgent Action Required", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
  };

  const renderCompetitor = (competitor, index) => {
    const strengths = competitor.strengths || [];
    return (
      <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-100" data-testid={`competitor-${index}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#0a2540] flex items-center justify-center text-white text-sm font-semibold">
              {competitor.rank}
            </div>
            <span className="font-semibold text-[#0a2540]">{competitor.name}</span>
          </div>
          <Badge className="bg-[#3ab046]/10 text-[#3ab046] border border-[#3ab046]/20">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Visible
          </Badge>
        </div>
        <p className="text-sm text-slate-600 mb-2">{competitor.why_visible}</p>
        <div className="flex flex-wrap gap-2">
          {strengths[0] && <Badge variant="outline" className="text-xs">{strengths[0]}</Badge>}
          {strengths[1] && <Badge variant="outline" className="text-xs">{strengths[1]}</Badge>}
          {strengths[2] && <Badge variant="outline" className="text-xs">{strengths[2]}</Badge>}
        </div>
      </div>
    );
  };

  const renderGap = (gap, index) => {
    const borderColor = gap.priority === 'Critical' ? '#7c2d12' : gap.priority === 'High' ? '#d41414' : gap.priority === 'Medium' ? '#d4af37' : '#3ab046';
    const priorityClass = gap.priority === 'Critical' ? 'bg-red-900/10 text-red-900 border-red-900/20' : gap.priority === 'High' ? 'priority-high' : gap.priority === 'Medium' ? 'priority-medium' : 'priority-low';
    
    return (
      <div key={index} className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor }} data-testid={`gap-${index}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge className={priorityClass}>{gap.priority}</Badge>
            <Badge variant="outline">{gap.category}</Badge>
          </div>
        </div>
        <p className="font-medium text-[#0a2540] mb-2 leading-relaxed">{gap.issue}</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          <span className="font-medium text-[#0a2540]">→ Action:</span> {gap.recommendation}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-[#0a2540]/20 border-t-[#d4af37] animate-spin" />
          <p className="text-slate-600">Loading your report...</p>
        </div>
      </div>
    );
  }

  if (!audit) return null;

  const scoreStatus = getScoreStatus(audit.visibility_score);
  const competitors = audit.competitors || [];
  const gaps = audit.gaps || [];
  const visibleCompetitors = isUnlocked ? competitors : competitors.slice(0, 2);
  const visibleGaps = isUnlocked ? gaps : gaps.slice(0, 2);
  const hasMoreCompetitors = !isUnlocked && competitors.length > 2;
  const hasMoreGaps = !isUnlocked && gaps.length > 2;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate("/audit")} className="flex items-center gap-2 text-slate-600 hover:text-[#0a2540] transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
              <span>New Audit</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]">PromptReady</span>
            </div>
            <div className="flex items-center gap-3">
              {isUnlocked && (
                <>
                  <Button onClick={handleCopyShareLink} variant="outline" className="border-[#0a2540] text-[#0a2540] hover:bg-[#0a2540] hover:text-white rounded-full" data-testid="share-report-btn">
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                    {copied ? "Copied!" : "Share"}
                  </Button>
                  <Button onClick={handleDownloadPDF} variant="outline" className="border-[#0a2540] text-[#0a2540] hover:bg-[#0a2540] hover:text-white rounded-full" data-testid="download-pdf-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12" ref={reportRef}>
        {/* Report Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <Badge className={`${scoreStatus.color} border px-3 py-1`} data-testid="visibility-status-badge">
              {audit.is_visible ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              {scoreStatus.label}
            </Badge>
            <span className="text-sm text-slate-500">
              Report generated {new Date(audit.timestamp).toLocaleDateString()}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0a2540]">AI Visibility Audit Report</h1>
          <p className="mt-2 text-lg text-slate-600">
            Analysis for <span className="font-semibold">{audit.business_name}</span>
          </p>
        </div>

        {/* Business Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8 animate-fade-in-up animate-delay-100">
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Business</p>
                <p className="font-medium text-[#0a2540]">{audit.business_name}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Service</p>
                <p className="font-medium text-[#0a2540] capitalize">{audit.service_type.replace('-', ' ')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Location</p>
                <p className="font-medium text-[#0a2540]">{audit.location}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] animate-fade-in-up animate-delay-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af37]" />
                  Visibility Score
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="relative w-40 h-40 mx-auto">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke={audit.visibility_score >= 70 ? "#3ab046" : audit.visibility_score >= 40 ? "#d4af37" : "#d41414"} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${audit.visibility_score * 2.83} 283`} transform="rotate(-90 50 50)" className="transition-all duration-1000" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-4xl font-bold ${getScoreColor(audit.visibility_score)}`} data-testid="visibility-score">{audit.visibility_score}%</span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600">
                    {audit.visibility_score < 20 && "URGENT: Your business is invisible to AI - customers are being sent to competitors"}
                    {audit.visibility_score >= 20 && audit.visibility_score < 40 && "CRITICAL: Your business is rarely recommended - significant opportunities being lost"}
                    {audit.visibility_score >= 40 && audit.visibility_score < 60 && "WARNING: Major visibility gaps - competitors are capturing your potential customers"}
                    {audit.visibility_score >= 60 && audit.visibility_score < 80 && "Your business has gaps in AI visibility - improvements needed to maximize leads"}
                    {audit.visibility_score >= 80 && "Minor gaps remain - quick wins available to capture more AI-driven leads"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a2540] text-white rounded-xl border-0 shadow-lg animate-fade-in-up animate-delay-300">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Query Analyzed</p>
                <p className="font-mono text-lg text-[#d4af37]" data-testid="query-analyzed">"{audit.query_used}"</p>
                <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                  This mirrors how 73% of consumers ask AI for local service recommendations. AI assistants interpret variations of this query the same way.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] animate-fade-in-up animate-delay-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#d4af37]" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-slate-600 leading-relaxed" data-testid="audit-summary">{audit.summary}</p>
              </CardContent>
            </Card>

            {/* Google SEO & Reviews Section */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] animate-fade-in-up animate-delay-250">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#d4af37]" />
                  Google Search & Reviews
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">How you rank in traditional Google search</p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Search className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-[#0a2540]">Google Search Ranking</span>
                    </div>
                    {audit.google_seo_found ? (
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-bold text-[#3ab046]">#{audit.google_seo_rank}</span>
                        <span className="text-slate-600">in search results</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="text-slate-600">Not found in top 10</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-[#0a2540]">Your Google Reviews</span>
                    </div>
                    {audit.google_review_count ? (
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-bold text-[#0a2540]">{audit.google_review_count}</span>
                        <div className="flex flex-col">
                          <span className="text-slate-600">reviews</span>
                          {audit.google_rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span className="text-sm font-medium">{audit.google_rating}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-500">No review data found</span>
                    )}
                  </div>
                </div>
                {isUnlocked && audit.competitor_reviews && audit.competitor_reviews.length > 0 ? (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <p className="font-medium text-[#0a2540] mb-3">Competitor Reviews</p>
                    <div className="space-y-2">
                      {audit.competitor_reviews.slice(0, 3).map((c, i) => (
                        <div key={i} className="flex justify-between p-2 bg-slate-50 rounded">
                          <span>{c.name}</span>
                          <span>{c.review_count || '?'} reviews {c.rating ? `(${c.rating}★)` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] animate-fade-in-up animate-delay-300">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#d4af37]" />
                  Competitors Being Surfaced
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">These businesses appear when AI is asked about your service</p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {visibleCompetitors[0] && renderCompetitor(visibleCompetitors[0], 0)}
                  {visibleCompetitors[1] && renderCompetitor(visibleCompetitors[1], 1)}
                  {isUnlocked && visibleCompetitors[2] && renderCompetitor(visibleCompetitors[2], 2)}
                  {isUnlocked && visibleCompetitors[3] && renderCompetitor(visibleCompetitors[3], 3)}
                  {isUnlocked && visibleCompetitors[4] && renderCompetitor(visibleCompetitors[4], 4)}
                  
                  {hasMoreCompetitors && (
                    <div className="relative">
                      <div className="blur-locked p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-full bg-slate-300" />
                          <span className="font-semibold">Hidden Competitor Name</span>
                        </div>
                        <p className="text-sm">Why this competitor ranks higher than you...</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Button onClick={() => setShowEmailModal(true)} className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] rounded-full shadow-lg" data-testid="unlock-competitors-btn">
                          <Lock className="w-4 h-4 mr-2" />
                          View Full Report
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] animate-fade-in-up animate-delay-400">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#d4af37]" />
                  Gap Analysis & Recommendations
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">What you need to improve to increase AI visibility</p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {visibleGaps[0] && renderGap(visibleGaps[0], 0)}
                  {visibleGaps[1] && renderGap(visibleGaps[1], 1)}
                  {isUnlocked && visibleGaps[2] && renderGap(visibleGaps[2], 2)}
                  {isUnlocked && visibleGaps[3] && renderGap(visibleGaps[3], 3)}
                  {isUnlocked && visibleGaps[4] && renderGap(visibleGaps[4], 4)}
                  {isUnlocked && visibleGaps[5] && renderGap(visibleGaps[5], 5)}
                  
                  {hasMoreGaps && (
                    <div className="relative">
                      <div className="blur-locked p-4 border-l-4 border-slate-300 bg-white rounded-r-lg shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-slate-100">Priority</Badge>
                        </div>
                        <p className="font-medium">Hidden gap analysis...</p>
                        <p className="text-sm">Hidden recommendation...</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Button onClick={() => setShowEmailModal(true)} className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] rounded-full shadow-lg" data-testid="unlock-gaps-btn">
                          <Lock className="w-4 h-4 mr-2" />
                          View Full Report
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {!isUnlocked && (
              <Card className="bg-gradient-to-r from-[#0a2540] to-[#162536] text-white rounded-xl border-0 shadow-xl animate-fade-in-up animate-delay-500">
                <CardContent className="p-8 text-center">
                  <Lock className="w-12 h-12 mx-auto mb-4 text-[#d4af37]" />
                  <h3 className="font-heading text-2xl font-semibold mb-2">Unlock Your Full Report</h3>
                  <p className="text-slate-300 mb-6 max-w-md mx-auto">Get complete competitor analysis, all gap recommendations, and download your shareable PDF report.</p>
                  <Button onClick={() => setShowEmailModal(true)} className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6" data-testid="unlock-full-report-btn">
                    <Unlock className="w-5 h-5 mr-2" />
                    Unlock Full Report Free
                  </Button>
                </CardContent>
              </Card>
            )}

            {isUnlocked && !showThankYou && (
              <Card className="bg-[#3ab046]/10 border border-[#3ab046]/20 rounded-xl animate-fade-in-up">
                <CardContent className="p-6 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-[#3ab046]" />
                  <h3 className="font-heading text-xl font-semibold text-[#0a2540] mb-2">Full Report Unlocked!</h3>
                  <p className="text-slate-600 mb-4">You now have access to all insights and can download your PDF report.</p>
                  <Button onClick={handleDownloadPDF} className="bg-[#0a2540] text-white hover:bg-[#162536] rounded-full" data-testid="download-report-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </Button>
                </CardContent>
              </Card>
            )}

            {isUnlocked && showThankYou && (
              <Card className="bg-gradient-to-br from-[#0a2540] to-[#162536] text-white rounded-xl border-0 shadow-xl animate-fade-in-up">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#3ab046] flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold mb-3">Thank You!</h3>
                  <p className="text-slate-300 mb-6 max-w-md mx-auto">
                    Your full AI Visibility Report is now unlocked. We've also sent a copy to your email.
                  </p>
                  
                  <div className="space-y-4">
                    <Button 
                      onClick={handleDownloadPDF} 
                      variant="outline"
                      className="w-full border-white/30 text-white hover:bg-white/10 rounded-full py-5"
                      data-testid="download-report-thankyou-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Your PDF Report
                    </Button>
                    
                    <div className="relative py-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[#162536] px-4 text-sm text-slate-400">Ready to fix these gaps?</span>
                      </div>
                    </div>
                    
                    <a 
                      href={bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid="book-consultation-btn"
                    >
                      <Button className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                        <Calendar className="w-5 h-5 mr-2" />
                        See Your Visibility Roadmap
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    
                    <p className="text-xs text-slate-400 mt-4">
                      15-minute call: We'll show you exactly what to fix first and how long it takes to outrank {audit.competitors?.[0]?.name || 'your competitors'}
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => setShowThankYou(false)}
                    className="mt-6 text-sm text-slate-400 hover:text-white underline"
                  >
                    View full report details
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#0a2540]">Unlock Your Full Report</DialogTitle>
            <DialogDescription className="text-slate-600">Enter your email to access the complete gap analysis, all competitor insights, and your downloadable PDF report.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#0a2540]">Business Email</Label>
              <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540]" data-testid="email-input" />
            </div>
            <Button onClick={handleUnlock} disabled={submittingEmail} className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] font-semibold rounded-full py-6" data-testid="submit-email-btn">
              {submittingEmail ? "Unlocking..." : (<><Mail className="w-5 h-5 mr-2" />Unlock Full Report</>)}
            </Button>
            <p className="text-xs text-slate-500 text-center">We'll send you a copy of the report. No spam, ever.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportPage;
